import TextButton from '@oberoninternal/travelbase-ds/components/action/TextButton';
import Body, { BodyInner } from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Special from '@oberoninternal/travelbase-ds/svg/Special';
import validInterval from '@oberoninternal/travelbase-ds/utils/validInterval';
import { Box, Flex } from '@rebass/grid';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import endOfMonth from 'date-fns/endOfMonth';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfMonth from 'date-fns/startOfMonth';
import isEqual from 'lodash.isequal';
import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Manager, Reference } from 'react-popper';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';

import styled from 'styled-components';
import { StringParam } from 'use-query-params';
import parseToDateString from '../constants/parseToDateString';
import { Booking } from '../entities/Booking';
import { AccommodationTypeEnum, PlannerTripFragment, RentalUnitTypeEnum, TripTypeEnum } from '../generated/graphql';
import usePlannerFetcher from '../hooks/planner/usePlannerFetcher';
import { PlannerHook } from '../hooks/planner/usePlannerPeriod';
import useTripPlannerQuery from '../hooks/tripPlanner/useTripPlannerQuery';
import usePrevious from '../hooks/usePrevious';
import useQueryParams from '../hooks/useQueryParams';
import { getPeriodColor } from '../utils/planner';
import {
    convertToDateStrings,
    convertToPeriod,
    filterTripsWithInterval,
    getTripDuration,
    getTripsFromPeriod,
    withDefaultBookingValues,
} from '../utils/trip';
import AvailableSpecials from './AvailableSpecials';
import Divider from './Divider';
import MemoizedPlannerMonth from './MemoizedPlannerMonth';
import Planner, { PlannerContainer, PlannerHeader, PlannerMonthInstance } from './Planner';
import PlannerControls from './PlannerControls';
import PlannerHelp from './PlannerHelp';
import PlannerInfoText from './PlannerInfoText';
import SpecialFilterInput from './SpecialFilterInput';
import TripGroupInput from './TripGroupInput';
import UnitStickyBar from './UnitStickyBar';

export interface PlannerProps extends PlannerHook {
    slug: string;
    booking: Booking;
    setBooking: (booking: Booking) => void;
    maxOccupancy?: number;
    minOccupancy?: number;
    petsAllowed?: number;
    headerChildren?: ReactNode;
    lazyLoading?: boolean;
    hideExplanation?: boolean;
    type?: 'accommodation' | 'unit';
    iframe?: boolean;
    reserving?: boolean;
    initialCenterMonth?: Date | number;
    hasInlineReservationBar?: boolean;
    onSubmit?: () => void;
    isolated?: boolean;
}

const defaultInitialStart = startOfMonth(new Date());

const pricedPerNightAccommodationTypes = [
    AccommodationTypeEnum.BedAndBreakfast,
    AccommodationTypeEnum.ApartmentBlock,
    AccommodationTypeEnum.Hotel,
];

const TripPlanner: FC<React.PropsWithChildren<PlannerProps>> = ({
    slug,
    booking,
    setBooking,
    state,
    onClick: dayClickHandler,
    onHover: dayHoverHandler,
    set,
    clear,
    maxOccupancy,
    minOccupancy,
    petsAllowed,
    headerChildren,
    initialState,
    lazyLoading,
    reserving,
    hideExplanation = false,
    iframe,
    type = 'unit',
    initialCenterMonth,
    hasInlineReservationBar,
    onSubmit,
}) => {
    const {
        amountAdults,
        amountChildren,
        amountPets,
        amountBabies,
        amountYouths,
        arrivalDate,
        departureDate,
        trip: selectedTrip,
    } = withDefaultBookingValues(booking);
    const viewportRef = useRef<PlannerMonthInstance | null>(null);
    const { period, selecting } = state;
    const totalPersonsAmount = amountAdults + amountChildren + amountYouths;
    const [{ specialId: specialFilter }] = useQueryParams({ specialId: StringParam });
    const travelGroup = useMemo(
        () => ({ persons: totalPersonsAmount, pets: amountPets, babies: amountBabies }),
        [amountBabies, amountPets, totalPersonsAmount]
    );
    const initialTravelGroup = useRef(travelGroup);
    const prevTravelGroup = usePrevious(travelGroup);
    const [{ data, variables }, { readQuery, writeQuery, query }] = useTripPlannerQuery({
        slug,
        start: parseToDateString(startOfMonth(initialState.period?.start ?? initialCenterMonth ?? defaultInitialStart)),
        end: parseToDateString(endOfMonth(initialState.period?.start ?? initialCenterMonth ?? defaultInitialStart)),
        ...initialTravelGroup.current,
    });

    // Only rooms and hotel-like accommodations are priced per night, holiday homes keep showing the total price of the stay
    const rentalUnit = data?.rentalUnit;
    const showPricePerNight =
        !!rentalUnit &&
        (rentalUnit.type === RentalUnitTypeEnum.Room ||
            pricedPerNightAccommodationTypes.includes(rentalUnit.accommodation.type));
    const allTrips = useMemo(() => data?.rentalUnit?.trips ?? [], [data?.rentalUnit?.trips]);
    const trips = specialFilter ? allTrips.filter(trip => trip.specialId === specialFilter) : allTrips;

    const fetcher = useCallback(
        async (months: Date[]) => {
            // normally we'd use fetchMore for this but somehow it doesn't look in the cache first.
            // also because we can only fetch 1 month per request we'll batch them and execute them in parallel.
            const promises: Promise<PlannerTripFragment[]>[] = [];
            for (const month of months) {
                const vars = {
                    start: parseToDateString(month),
                    end: parseToDateString(endOfMonth(month)),
                    ...travelGroup,
                };

                const execute = async () => {
                    const mergedVars = { ...variables, ...vars };
                    const result = await query(mergedVars);

                    // as all trips get added to the main query we need to filter the trips only for that month in
                    // order to avoid  duplicates

                    const isMain = isEqual(mergedVars, variables);
                    if (isMain) {
                        const monthInterval = {
                            start: new Date(variables.start),
                            end: new Date(variables.end),
                        };
                        return filterTripsWithInterval(result.data.rentalUnit?.trips ?? [], monthInterval);
                    }

                    return result.data.rentalUnit?.trips ?? [];
                };

                const promise = execute();
                promises.push(promise);
            }

            const results = await Promise.all(promises);

            const prev = readQuery();

            if (!prev?.rentalUnit) {
                return;
            }

            const monthsInterval = {
                start: startOfMonth(months[0]),
                end: startOfMonth(addMonths(months[months.length - 1], 1)),
            };

            writeQuery({
                ...prev,
                rentalUnit: {
                    ...prev?.rentalUnit,
                    trips: [
                        // we remove all trips for the requested months in order to avoid duplicates
                        ...filterTripsWithInterval(prev.rentalUnit?.trips ?? [], monthsInterval, true),
                        ...results.flat(),
                    ],
                },
            });
        },
        [query, readQuery, travelGroup, variables, writeQuery]
    );

    const [{ loading: fetching, loadedMonths }, { isLoaded, loadMore, invalidate }] = usePlannerFetcher(
        fetcher,
        lazyLoading
    );
    const loading = fetching || !!lazyLoading;
    const [monthsInView, setMonthsInView] = useState<Date[] | null>(null);

    const monthsInViewLoaded = useMemo(
        () => !!monthsInView && !loading && monthsInView.every(month => isLoaded(month)),
        [monthsInView, isLoaded, loading]
    );

    const cachedPeriod = useRef(period);

    // When the travelgroup changes we need to refetch the trips for the days in view
    useEffect(() => {
        if (!isEqual(travelGroup, prevTravelGroup)) {
            if (data?.rentalUnit?.id) {
                writeQuery({
                    ...data,
                    rentalUnit: {
                        ...data?.rentalUnit,
                        trips: [],
                    },
                });

                invalidate();
            }
        }
    }, [data, monthsInView, invalidate, prevTravelGroup, travelGroup, writeQuery]);

    // when invalidated we need to trigger the query manually again
    useEffect(() => {
        if (!loadedMonths && monthsInView) {
            loadMore(monthsInView);
        }
    }, [monthsInView, loadMore, loadedMonths]);

    const tripsInView = useMemo(() => {
        if (monthsInView && trips.length > 0) {
            const [start, end] = monthsInView;
            return trips.filter(trip =>
                isWithinInterval(new Date(trip.date), { start, end: addMonths(end ?? start, 1) })
            );
        }
        return [];
    }, [monthsInView, trips]);

    // Unique specials that are in view
    const specialsInView = tripsInView
        .map(trip => trip.specialId)
        .filter((specialId, i, self): specialId is string => !!specialId && self.indexOf(specialId) === i);

    const selectedTrips = useMemo(() => {
        if (selecting || !validInterval(period)) {
            return undefined;
        }

        return allTrips
            .filter(
                trip =>
                    isSameDay(new Date(trip.date), period.start) &&
                    isSameDay(addDays(new Date(trip.date), trip.duration), period.end)
            )
            .sort((a, b) => a.price - b.price);
    }, [period, selecting, allTrips]);

    const periodColor = getPeriodColor(selectedTrips ?? [], specialFilter);

    const startOfPeriod = period?.start ? +period.start : arrivalDate;

    useEffect(() => {
        if (selecting || !monthsInViewLoaded) {
            return;
        }

        const periodChanged = !isEqual(cachedPeriod.current, period);
        if (validInterval(period) && periodChanged && !selecting) {
            const formatted = convertToDateStrings(period);
            const [trip] = getTripsFromPeriod(period, trips) ?? [];
            setBooking({ ...formatted, trip: trip ?? selectedTrip });
            cachedPeriod.current = period;
        }
        // reset if we haven't selected a period
        else if (selectedTrip && !validInterval(period)) {
            setBooking({
                arrivalDate: undefined,
                departureDate: undefined,
                trip: undefined,
            });
            cachedPeriod.current = null;
        }
        // if we have selected a trip but its stale, replace it with the correct one
        else if (validInterval(period) && !periodChanged && selectedTrip) {
            const [trip] = getTripsFromPeriod(period, trips) ?? [];
            if (!isEqual(trip, selectedTrip)) {
                setBooking({
                    trip,
                });
            }
        }
        // only runs when the selected period or the data changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, data, monthsInViewLoaded]);

    const deselect = useCallback(() => {
        clear();
        setBooking({
            trip: undefined,
            arrivalDate: undefined,
            departureDate: undefined,
        });
        cachedPeriod.current = null;
    }, [clear, setBooking]);

    useEffect(() => {
        if (!monthsInViewLoaded) {
            return;
        }

        if (arrivalDate && departureDate && !selectedTrip && trips.length > 0) {
            const duration = getTripDuration(arrivalDate, departureDate);
            const [foundTrip] = trips
                .filter(trip => trip.date === arrivalDate && trip.duration === duration)
                .sort((a, b) => a.price - b.price);

            if (foundTrip) {
                setBooking({
                    trip: foundTrip,
                });
            }
        }
        if (arrivalDate && departureDate && !selectedTrip) {
            const periodFromBooking = convertToPeriod(arrivalDate, departureDate);
            const tripsFromPeriod = validInterval(periodFromBooking)
                ? getTripsFromPeriod(periodFromBooking, trips, true)
                : null;

            if (!tripsFromPeriod?.length) {
                deselect();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arrivalDate, departureDate, monthsInViewLoaded, data]);

    useEffect(() => {
        if (startOfPeriod && viewportRef.current) {
            viewportRef.current.plannerViewActions.jumpToMonth(new Date(startOfPeriod), true);
        }
    }, [startOfPeriod]);

    return (
        <PlannerContainer>
            <Planner
                monthsRef={viewportRef}
                isSelecting={selecting}
                period={period}
                trips={trips}
                showPricePerNight={showPricePerNight}
                isMonthLoaded={isLoaded}
                onDayClick={dayClickHandler}
                onDayMouseEnter={dayHoverHandler}
                defaultFontColor={periodColor}
                periodColor={periodColor}
                initialCenterMonth={period?.start ?? initialCenterMonth}
                onInViewIntervalChange={months => {
                    loadMore(months);
                    setMonthsInView(months);
                }}
                onClickOutside={() => {
                    if (selecting) {
                        set({ selecting: false, period: null });
                    }
                }}
                PlannerMonthComponent={MemoizedPlannerMonth}
            >
                <PlannerHeader padding={['2rem 2rem 0 2rem', null, 0]}>
                    {headerChildren}
                    <PlannerControls
                        mt={[null, null, 8]}
                        loading={loading}
                        mb={iframe ? [null, null, '1rem'] : [null, null, '8rem']}
                    >
                        <TripGroupWrapper>
                            <Body className="gt-s" style={{ whiteSpace: 'nowrap', marginRight: '0.8rem' }}>
                                <FormattedMessage defaultMessage="Reisgezelschap" />
                            </Body>
                            <StyledTripGroupInput
                                loading={loading}
                                booking={booking}
                                setBooking={setBooking}
                                width={[1, null, '20rem', '30rem']}
                                maxOccupancy={maxOccupancy}
                                minOccupancy={minOccupancy}
                                petsAllowed={petsAllowed}
                            />

                            <Manager>
                                <Reference>
                                    {({ ref }) => (
                                        <SpecialFilterInput
                                            specialIds={specialsInView}
                                            beforeOnChange={deselect}
                                            ref={ref}
                                        />
                                    )}
                                </Reference>

                                {monthsInViewLoaded && (
                                    <PlannerInfoText
                                        tripsInViewCount={tripsInView.length}
                                        specialFilterActive={!!specialFilter}
                                    />
                                )}
                            </Manager>
                        </TripGroupWrapper>
                        <Divider my={0} className="lt-s" />
                        <Flex width={1}>
                            <PlannerHelp
                                brand={data?.rentalUnit?.brand}
                                rentalUnitCode={data?.rentalUnit?.code}
                                rentalUnitName={data?.rentalUnit?.name}
                                className="lt-s"
                                bookingIsValid={!!(booking.arrivalDate && booking.departureDate)}
                                type={type}
                                reserving={reserving}
                                onBookingReservation={onSubmit}
                            />
                        </Flex>
                    </PlannerControls>
                </PlannerHeader>
            </Planner>
            {!hideExplanation && (
                <Flex className="gt-s" justifyContent="space-between" alignItems="center" width={1}>
                    <TextButton onClick={deselect} disabled={!period}>
                        <FormattedMessage defaultMessage="Selectie verwijderen" />
                    </TextButton>
                    {showPricePerNight && (
                        <Body variant="small">
                            <FormattedMessage defaultMessage="Prijzen in de kalender zijn per nacht." />
                        </Body>
                    )}
                    {tripsInView?.some(trip => trip.type !== TripTypeEnum.Regular && trip.originalPrice) && (
                        <SpecialText>
                            <Special />
                            <FormattedMessage defaultMessage="Op deze datum is een korting van toepassing." />
                        </SpecialText>
                    )}
                    <PlannerHelp
                        brand={data?.rentalUnit?.brand}
                        rentalUnitCode={data?.rentalUnit?.code}
                        rentalUnitName={data?.rentalUnit?.name}
                        bookingIsValid={!!(booking.arrivalDate && booking.departureDate)}
                        type={type}
                        reserving={reserving}
                        onBookingReservation={onSubmit}
                    />
                </Flex>
            )}
            {hasInlineReservationBar && (
                <UnitStickyBar
                    plannerIsOpen
                    booking={booking}
                    hasRelativePosition
                    confirmButton={
                        <Button
                            style={{ marginLeft: 'auto' }}
                            size="large"
                            variant="primary"
                            disabled={!booking.trip}
                            onClick={onSubmit}
                        >
                            <FormattedMessage defaultMessage="Reserveren" />
                        </Button>
                    }
                />
            )}
            <AvailableSpecials trips={selectedTrips} booking={booking} setBooking={setBooking} />
        </PlannerContainer>
    );
};

export default memo(TripPlanner);

export const Header = styled.div`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s + 1}px) {
        padding: 2rem 2rem 0 2rem;
        box-shadow: 0 0 40px 0 rgba(16, 36, 48, 0.1);
    }
`;

const SpecialText = styled.div`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.primary['80']};
    svg {
        margin-right: 0.5rem;
    }
`;

export const Container = styled(Box)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s + 1}px) {
        max-height: 100vh;
        height: 100%;
        display: flex;
        flex-direction: column;
    }
`;

const StyledTripGroupInput = styled(TripGroupInput)`
    --borderRadius: 3rem;
`;

const TripGroupWrapper = styled(Flex)`
    position: relative;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        flex-direction: row;
        align-items: center;
        > * + * {
            margin: 0 0 0 ${({ theme }) => theme.spacing['30_Small']};
        }
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s - 1}px) {
        column-gap: 0.8rem;
        width: 100%;
        padding-bottom: ${({ theme }) => theme.spacing['40_Standard']};
        justify-content: space-between;
        > * {
            flex: 1;
        }
        > ${BodyInner}:last-of-type {
            margin-left: 0.8rem;
        }
    }
`;
